import { RateFormatter, SummitFormattedText } from '@summit/models';

import { EboObject } from '../base-classes/ebo-object';
import { Hub } from '../places/hub/hub';
import { EProcessTypeMapping } from '../process/e-process-type.enum';
import { RemotePasswordFormat } from '../sync/remote-password-format';
import { DivisionGoal } from './division-goal';
import { DivisionHub } from './division-hub';
import { DivisionManager } from './division-manager';
import { DivisionParameter } from './division-parameter';
import { DivisionStatus } from './division-status.enum';
import { DivisionIncentive } from './e-wages/division-incentive';
import { DivisionWageRule } from './e-wages/division-wage-rule';
import { DivisionLegWage } from './lh-wages/division-leg-wage';
import { DivisionSpotWage } from './lh-wages/division-spot-wage';
import { DivisionPolicy } from './policy/division-policy';

export class Division extends EboObject {
  name: string;
  divisionManagers: DivisionManager[];
  divisionHubs: DivisionHub[];
  status: DivisionStatus;
  currentParameter: DivisionParameter;
  parameters: DivisionParameter[];
  wageRules: DivisionWageRule[];
  wageIncentives: DivisionIncentive[];
  legWages: DivisionLegWage[];
  spotWages: DivisionSpotWage[];
  currentGoal: DivisionGoal;
  goals: DivisionGoal[];
  remotePasswordFormat: RemotePasswordFormat;
  remotePasswordParameter: string;
  policies: DivisionPolicy[];

  constructor(jsonData?: Partial<Division>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.divisionManagers) {
        this.divisionManagers = jsonData.divisionManagers.map((data) => {
          return new DivisionManager(data);
        });
        this.divisionManagers = this.divisionManagers.sort((e1, e2) => {
          if (e1.employee.name < e2.employee.name) {
            return -1;
          }
          if (e1.employee.name > e2.employee.name) {
            return 1;
          }
          return 0;
        });
      } else {
        this.divisionManagers = new Array<DivisionManager>();
      }
      if (jsonData.divisionHubs) {
        this.divisionHubs = jsonData.divisionHubs
          .map((data) => {
            return new DivisionHub(data);
          })
          .sort((a: DivisionHub, b: DivisionHub) =>
            a.toShortString('hubToShortString') > b.toShortString('hubToShortString') ? 1 : -1
          );
      }
      if (jsonData.currentParameter) {
        //console.log(`Division Creating Parameter : ${jsonData.currentParameter}`);
        this.currentParameter = new DivisionParameter(jsonData.currentParameter);
      }
      if (jsonData.parameters) this.parameters = jsonData.parameters.map((data) => new DivisionParameter(data));
      if (jsonData.goals) this.goals = jsonData.goals.map((data) => new DivisionGoal(data));
      if (jsonData.currentGoal) this.currentGoal = new DivisionGoal(jsonData.currentGoal);
      if (jsonData.legWages) this.legWages = jsonData.legWages.map((data) => new DivisionLegWage(data));
      if (jsonData.spotWages) this.spotWages = jsonData.spotWages.map((data) => new DivisionSpotWage(data));
      if (jsonData.policies) this.policies = jsonData.policies.map((data) => new DivisionPolicy(data));
      if (jsonData.wageRules) this.wageRules = jsonData.wageRules.map((data) => new DivisionWageRule(data));
      if (jsonData.wageIncentives)
        this.wageIncentives = jsonData.wageIncentives.map((data) => new DivisionIncentive(data));
    }
    this.divisionHubs ??= new Array<DivisionHub>();
    this.parameters ??= new Array<DivisionParameter>();
    this.goals ??= new Array<DivisionGoal>();
    this.legWages ??= new Array<DivisionLegWage>();
    this.spotWages ??= new Array<DivisionSpotWage>();
    this.policies ??= new Array<DivisionPolicy>();
    this.wageRules ??= new Array<DivisionWageRule>();
    this.wageIncentives ??= new Array<DivisionIncentive>();
  }

  get hubs(): Hub[] {
    return this.divisionHubs?.map((entry) => entry.hub);
  }
  toShortString() {
    return this.name;
  }

  isCorporate(): boolean {
    return this.divisionHubs.findIndex((item) => item.hubID == 0) >= 0;
  }

  isDeprecated(): boolean {
    return this.status == DivisionStatus.Archived;
  }

  getNextSpotWageRefNumber(): number {
    let maxRuleNum = 0;
    if (this.spotWages && this.spotWages.length > 0) {
      maxRuleNum = this.spotWages
        .map((s) => s.referenceNumber)
        .reduce(function (a, b) {
          return Math.max(a, b);
        });
    }
    return (maxRuleNum ?? 0) + 1;
  }

  get goalsFormatted(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.currentGoal) {
      response.AddLine(`As of ${this.currentGoal.effectiveDate.format('M/D/YY')}`);
      response.AddLine(`Payroll / Mile: ${RateFormatter(this.currentGoal.mileageRateGoal)}`);
    }
    return response;
  }

  get parametersFormatted(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.currentParameter) {
      response.AddLine(`As of ${this.currentParameter.effectiveDate.format('M/D/YY')}`);
      response.AddLine('Scheduling', ['flex', 'flex-row', 'justify-center', 'font-bold']);
      response.AddText(this.currentParameter.scheduleParametersString);
      response.AddLine('Digital Logs', ['flex', 'flex-row', 'justify-center', 'font-bold']);
      response.AddText(this.currentParameter.digitalLogParametersSummary);
      response.AddLine('Payroll', ['flex', 'flex-row', 'justify-center', 'font-bold']);
      response.AddText(this.currentParameter.payRollParametersSummary);
      response.AddLine('Reconcile Parameters', ['flex', 'flex-row', 'justify-center', 'font-bold']);
      response.AddText(this.currentParameter.getReconcileParametersSummary(this.isCorporate()));
      response.AddLine('Other Parameters', ['flex', 'flex-row', 'justify-center', 'font-bold']);
      response.AddText(this.currentParameter.otherParametersSummary);
    }
    return response;
  }

  get policiesSummary(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.policies && this.policies.length > 0) {
      this.policies.forEach((policy) => {
        response.AddLine(EProcessTypeMapping[policy.group], [
          'bold',
          'flex',
          'flex-row',
          'justify-center',
          'surface-300'
        ]);
        response.AddText(policy.toSummary());
      });
    }
    return response;
  }

  private _activeWages: SummitFormattedText;
  get activeWagesSummary(): SummitFormattedText {
    if (!this._activeWages) {
      this._activeWages = new SummitFormattedText();
      const activeWageRules = this.wageRules.filter((rule) => rule.isActive());
      if (activeWageRules.length > 0) {
        this._activeWages.AddLine('Active Wage Rules', ['bold', 'flex', 'flex-row', 'justify-center']);
        activeWageRules.forEach((rule) => {
          this._activeWages.AddText(rule.applicableEmployee);
          this._activeWages.AddText(rule.paySummary);
        });
      }
      const activeWageIncentives = this.wageIncentives.filter((rule) => rule.isActive());
      //console.log('Active Incentives', activeWageIncentives);
      if (activeWageIncentives.length > 0) {
        this._activeWages.AddLine('Active Incentives', ['bold', 'flex', 'flex-row', 'justify-center']);
        activeWageIncentives.forEach((rule) => {
          this._activeWages.AddKeyValues(rule.applicableEmployee.toString(), rule.incentive.name);
        });
      }
    }
    return this._activeWages;
  }
  set activeWagesSummary(value: SummitFormattedText) {
    //nop
  }
}
